import api from './api';
import logger from '../utils/logger';

export interface AlarmRule {
  id: string;
  orgId: string;
  deviceId?: string;
  zoneId?: string;
  type: string;
  severity: 'info' | 'warning' | 'critical';
  thresholdHigh?: number;
  thresholdLow?: number;
  hysteresis: number;
  debounceSec: number;
  cooldownSec: number;
  requiresAck: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AlarmRuleTemplate {
  id: string;
  name: string;
  nameAr: string;
  description: string;
  descriptionAr: string;
  category: string;
  rules: Array<{
    type: string;
    severity: 'info' | 'warning' | 'critical';
    thresholdLow?: number;
    thresholdHigh?: number;
    hysteresis: number;
    debounceSec: number;
    cooldownSec: number;
  }>;
}

class AlarmRulesApi {
  async getDeviceRules(deviceId: string): Promise<AlarmRule[]> {
    try {
      const response = await api.get(`/alarm-rules/device/${deviceId}`);
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch alarm rules:', error);
      throw error;
    }
  }

  async getTemplates(): Promise<AlarmRuleTemplate[]> {
    try {
      const response = await api.get('/alarm-rules/templates');
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch templates:', error);
      throw error;
    }
  }

  async applyTemplate(
    deviceId: string,
    templateId: string,
  ): Promise<{ template: string; rulesCreated: number }> {
    try {
      const response = await api.post('/alarm-rules/apply-template', { deviceId, templateId });
      return response.data;
    } catch (error: any) {
      logger.error('Failed to apply template:', error);
      throw new Error(error.response?.data?.message || 'Failed to apply template');
    }
  }

  async createRule(ruleData: {
    deviceId: string;
    type: string;
    severity?: 'info' | 'warning' | 'critical';
    thresholdHigh?: number;
    thresholdLow?: number;
    hysteresis?: number;
    debounceSec?: number;
    cooldownSec?: number;
    requiresAck?: boolean;
  }): Promise<AlarmRule> {
    try {
      const response = await api.post('/alarm-rules', ruleData);
      return response.data;
    } catch (error: any) {
      logger.error('Failed to create rule:', error);
      throw new Error(error.response?.data?.message || 'Failed to create rule');
    }
  }

  async updateRule(
    ruleId: string,
    ruleData: Partial<{
      type: string;
      severity: 'info' | 'warning' | 'critical';
      thresholdHigh: number;
      thresholdLow: number;
      hysteresis: number;
      debounceSec: number;
      cooldownSec: number;
      requiresAck: boolean;
    }>,
  ): Promise<AlarmRule> {
    try {
      const response = await api.put(`/alarm-rules/${ruleId}`, ruleData);
      return response.data;
    } catch (error: any) {
      logger.error('Failed to update rule:', error);
      throw new Error(error.response?.data?.message || 'Failed to update rule');
    }
  }

  async deleteRule(ruleId: string): Promise<void> {
    try {
      await api.delete(`/alarm-rules/${ruleId}`);
    } catch (error: any) {
      logger.error('Failed to delete rule:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete rule');
    }
  }

  // ==================== Custom Templates ====================

  async getCustomTemplates(): Promise<AlarmRuleTemplate[]> {
    try {
      const response = await api.get('/alarm-rules/custom-templates');
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch custom templates:', error);
      throw error;
    }
  }

  async getCustomTemplate(templateId: string): Promise<AlarmRuleTemplate> {
    try {
      const response = await api.get(`/alarm-rules/custom-templates/${templateId}`);
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch custom template:', error);
      throw error;
    }
  }

  async createCustomTemplate(template: Partial<AlarmRuleTemplate>): Promise<AlarmRuleTemplate> {
    try {
      const response = await api.post('/alarm-rules/custom-templates', template);
      return response.data;
    } catch (error: any) {
      logger.error('Failed to create custom template:', error);
      throw new Error(error.response?.data?.message || 'Failed to create custom template');
    }
  }

  async updateCustomTemplate(
    templateId: string,
    template: Partial<AlarmRuleTemplate>,
  ): Promise<AlarmRuleTemplate> {
    try {
      const response = await api.put(`/alarm-rules/custom-templates/${templateId}`, template);
      return response.data;
    } catch (error: any) {
      logger.error('Failed to update custom template:', error);
      throw new Error(error.response?.data?.message || 'Failed to update custom template');
    }
  }

  async deleteCustomTemplate(templateId: string): Promise<void> {
    try {
      await api.delete(`/alarm-rules/custom-templates/${templateId}`);
    } catch (error: any) {
      logger.error('Failed to delete custom template:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete custom template');
    }
  }

  async applyCustomTemplate(
    templateId: string,
    deviceId: string,
  ): Promise<{ template: string; rulesCreated: number }> {
    try {
      const response = await api.post(
        `/alarm-rules/custom-templates/${templateId}/apply/${deviceId}`,
      );
      return response.data;
    } catch (error: any) {
      logger.error('Failed to apply custom template:', error);
      throw new Error(error.response?.data?.message || 'Failed to apply custom template');
    }
  }
}

export const alarmRulesApi = new AlarmRulesApi();
